import * as React from "react";
import type { CSSProperties, FunctionComponent, ReactNode } from "react";

import noop from "lodash/noop";
import Modal from "react-modal";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, rgba } from "@similarweb/styles";

export interface IProModalCustomStyles {
    overlay?: CSSProperties;
    content?: CSSProperties;
}

export interface IProModal {
    shouldCloseOnOverlayClick?: boolean;
    isOpen?: boolean;
    customStyles?: IProModalCustomStyles;
    showCloseIcon?: boolean;
    overlayClassName?: string | Modal.Classes;
    portalClassName?: string;
    className?: string | Modal.Classes;
    closeIconColor?: string;
    closeTimeoutMS?: number;
    onCloseClick?(): void;
    onAfterOpen?(): void;
    onAfterClose?(): void;
    overlayRef?(el: any): any;
    contentRef?(el: any): any;
    children?: ReactNode;
}

export const CloseIconWrapper = styled.div`
    position: absolute;
    top: 18px;
    right: 18px;
    height: 24px;
    width: 24px;
    cursor: pointer;
    svg > path {
        fill: ${(props) => props.color};
    }
`;
CloseIconWrapper.displayName = "CloseIconWrapper";

export const CloseIcon: FunctionComponent<{
    onClick: VoidFunction;
    color?: string;
    className?: string;
}> = ({ onClick, color, className }) => (
    <CloseIconWrapper
        className={className}
        onClick={onClick}
        color={color}
    >
        <SWReactIcons iconName="clear" />
    </CloseIconWrapper>
);

const overlayElement = (
    props: React.ComponentPropsWithRef<"div">,
    contentElement: React.ReactElement,
): React.ReactElement => (
    <div
        {...props}
        data-react-aria-top-layer="true"
    >
        {contentElement}
    </div>
);

const defaultStyles: IProModalCustomStyles = {
    overlay: {
        overflowY: "auto",
        zIndex: 1120,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        right: "auto",
        bottom: "auto",
        padding: "16px",
        backgroundColor: rgba(colorsPalettes.midnight[300], 0.7),
    },
    content: {
        overflow: "visible",
        position: "relative",
        boxSizing: "border-box",
        flexShrink: 0,
        width: "566px",
        height: "auto",
        top: "auto",
        right: "auto",
        bottom: "auto",
        left: "auto",
        margin: "auto",
        padding: "30px",
        borderRadius: "6px",
        backgroundColor: "#fff",
        boxShadow: "0 12px 30px 0 rgba(0, 0, 0, 0.3)",
    },
};

/**
 * @deprecated. please use StructureModal or BasicModal "@similarweb/ui-components" instead
 */
export class ProModal extends React.PureComponent<IProModal> {
    public static defaultProps = {
        onCloseClick: noop,
        onAfterOpen: noop,
        onAfterClose: noop,
        isOpen: false,
        shouldCloseOnOverlayClick: true,
        customStyles: {
            overlay: {},
            content: {},
        },
        showCloseIcon: true,
        closeTimeoutMS: 150,
    };

    public componentDidMount() {
        if (this.props.isOpen) {
            document.addEventListener("keyup", this.handleKeyUp, { once: true, capture: true });
        }
    }

    private onCloseClick = () => {
        this.props.onCloseClick?.();
    };

    private handleKeyUp = (e: KeyboardEvent) => {
        if (e.keyCode === 27) {
            this.onCloseClick();
        }
    };

    public render() {
        const {
            isOpen,
            customStyles,
            shouldCloseOnOverlayClick,
            onAfterOpen,
            onAfterClose,
            overlayRef,
            contentRef,
            portalClassName,
            className,
            closeIconColor,
            closeTimeoutMS,
            overlayClassName,
        } = this.props;
        const styles: IProModalCustomStyles = {
            overlay: {
                ...(overlayClassName ? Modal.defaultStyles.overlay : {}),
                ...defaultStyles.overlay,
                ...customStyles?.overlay,
            },
            content: {
                ...defaultStyles.content,
                ...customStyles?.content,
            },
        };

        return (
            <Modal
                // @ts-ignore FIXME: strict migration - No overload matches this call — isOpen is boolean|undefined (guaranteed via defaultProps), react-modal expects boolean
                isOpen={isOpen}
                style={styles}
                onRequestClose={this.onCloseClick}
                onAfterOpen={onAfterOpen}
                onAfterClose={onAfterClose}
                shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
                closeTimeoutMS={closeTimeoutMS}
                ariaHideApp={false}
                overlayRef={overlayRef}
                contentRef={contentRef}
                portalClassName={portalClassName}
                className={className}
                overlayClassName={overlayClassName}
                overlayElement={overlayElement}
                data={{ automation: "pro-modal" }}
            >
                {this.props.children}
                {this.props.showCloseIcon ? (
                    <CloseIcon
                        onClick={this.onCloseClick}
                        color={closeIconColor}
                    />
                ) : null}
            </Modal>
        );
    }
}
